<script setup>
// Big clickable cards for choosing a path (e.g. which portal to sign in
// to): optional picture, title and a short description. Emits `select`.
defineProps({
  items: { type: Array, required: true }, // [{ id, title, description?, image?, icon? }]
});
defineEmits(['select']);
</script>

<template>
  <div class="ui-choices">
    <button
      v-for="item in items"
      :key="item.id"
      type="button"
      class="ui-choices__card"
      @click="$emit('select', item)"
    >
      <span v-if="item.image" class="ui-choices__media">
        <img :src="item.image" alt="" />
      </span>
      <span class="ui-choices__body">
        <span class="ui-choices__title">
          <v-icon v-if="item.icon" size="18">{{ item.icon }}</v-icon>
          {{ item.title }}
        </span>
        <span v-if="item.description" class="ui-choices__description">{{ item.description }}</span>
      </span>
      <v-icon size="18" class="ui-choices__arrow">mdi-arrow-right</v-icon>
    </button>
  </div>
</template>

<style scoped>
.ui-choices {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 12px;
}
.ui-choices__card {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  padding: 0;
  border: 1px solid var(--border);
  border-radius: 12px;
  background: var(--card);
  color: var(--foreground);
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.ui-choices__card:hover {
  border-color: rgb(var(--v-theme-primary));
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
}
.ui-choices__card:focus-visible {
  outline: 2px solid var(--brand);
  outline-offset: 2px;
}
.ui-choices__media {
  display: block;
  aspect-ratio: 16 / 9;
  background: var(--subtle);
}
.ui-choices__media img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}
.ui-choices__card:hover .ui-choices__media img {
  transform: scale(1.03);
}
.ui-choices__body {
  display: flex;
  flex-direction: column;
  gap: 4px;
  padding: 14px 40px 16px 16px;
}
.ui-choices__title {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 15px;
  font-weight: 600;
}
.ui-choices__title .v-icon {
  color: var(--muted-foreground);
}
.ui-choices__description {
  font-size: 13px;
  line-height: 1.5;
  color: var(--muted-foreground);
}
.ui-choices__arrow {
  position: absolute;
  right: 14px;
  bottom: 18px;
  color: var(--muted-foreground);
  transition: transform 0.15s ease, color 0.15s ease;
}
.ui-choices__card:hover .ui-choices__arrow {
  transform: translateX(2px);
  color: rgb(var(--v-theme-primary));
}
</style>
